import {
   parseQueryString
  ,stringifyQueryString 
  ,useNavigate
  ,matchPath
  ,generatePath
  ,useLocation
  ,PathMatch
  ,useMemo
}
from 'external/react'
import { pickBy, transform, mapValues, isUUID } from 'external/lodash'


interface RoutePatterns {
  [x: string] : string
}

interface MatchedRoutes {
  [x: string]: PathMatch<string>
}


type NavigationArgs = [override: any, ...trailing: string[]]

interface NavigationRoute {
  link: (...args:NavigationArgs) => string
  go: (...args:NavigationArgs) => void
  linkWithQuery: (overrideQuery: any, preserveQuery: boolean, ...args:NavigationArgs) => string
  goWithQuery: (overrideQuery: any, preserveQuery:boolean, ...args:NavigationArgs) => void
  active: boolean
}

interface Navigation {
  [x:string]: NavigationRoute
}

export default function useNavigation (routes:RoutePatterns) {
  const {search, pathname} = useLocation()
  const query = parseQueryString(search)
  const navigate = useNavigate()
  
  const matched: MatchedRoutes = useMemo(() => {
    return pickBy(mapValues(routes, pattern => matchPath(pattern, pathname)), match => match != null) as MatchedRoutes
  }, [pathname])

  const params = useMemo(() => {
    return transform(matched, (params, match) => Object.assign(params, {...match.params}), {})
  }, [matched])
  
  const navigation: Navigation = useMemo(() => {
    return mapValues(routes, (pattern, key) => {
      const link = (override: any, ...trailing: string[]) => buildPath(pattern, {params, override}, ...trailing)
      const go = (override: any, ...trailing: string[]) => navigate(buildPath(pattern, {params, override}, ...trailing))
      const goWithQuery = (overrideQuery: any, preserveQuery: boolean, override: any, ...trailing: string[]) => {
        navigate(buildQueryPath(pattern, {params, override, query, overrideQuery, preserveQuery}, ...trailing))
      }
      const linkWithQuery = (overrideQuery: any, preserveQuery: boolean, override: any, ...trailing: string[]) => {
         return buildQueryPath(pattern, {params, override, query, overrideQuery, preserveQuery}, ...trailing)
      }

      const active = !!matched[key]
      return {link, go, active, linkWithQuery, goWithQuery} as NavigationRoute
    })
  }, [matched, params, query, pathname])
  
  const active = useMemo(() => {
    return Object.values(navigation).map(({active}) => active).indexOf(true) >= 0
  }, [navigation])

  return {active, params, query, navigation}

}

type BuildPathOptions = [pattern: string, options: {
  params: any,
  override?: any,
  query?: any,
  overrideQuery?: any,
  preserveQuery?: boolean
}, ...trailing: string[]]

function buildQueryPath(...args: BuildPathOptions) : string {
  const [_, options] = args
  const path = buildPath(...args)  
  const query = options.preserveQuery
    ? Object.assign({}, options.query, options.overrideQuery)
    : options.overrideQuery
  return `${path}?${stringifyQueryString(query)}`
}

function buildPath(...args: BuildPathOptions) : string {
  const [pattern, options, ...trailing] = args
  const params = Object.assign({}, options.params, options.override)
  const pathname = generatePath(pattern, params)
  return trailing.length == 0
    ? pathname
    : `${pathname}/${trailing.join('/')}`
}

export function isLoggedInAndInRoute(route:string | string[], {state, contexts}) {
    const {id} = contexts.Auth.state.user || {}
    const {props: {navigation}} = state
    if (!isUUID(id)) return false
    const inRoute = Array.isArray(route) ? route.some(r => navigation[r].active) : navigation[route].active
    if (!inRoute) return false
	return id
}

export function isLoggedIn({state, contexts}) {
  const {id} = contexts.Auth.state.user || {}
  const {props: {navigation}} = state
  if (!isUUID(id)) return false
  return id
}